/**
 * Определение города для текущего сеанса Mini App.
 *
 * Источники:
 *  1. real-режим: window.WebApp.initDataUnsafe.start_param
 *     (передаётся через deeplink https://max.ru/<bot>?startapp=<city>).
 *     Если start_param пустой / невалидный → null. Никакого silent fallback
 *     на shakhty (см. types/city.ts комментарий о DEFAULT_CITY).
 *  2. mock-режим: VITE_MOCK_CITY из .env. Здесь fallback на DEFAULT_CITY
 *     разрешён (mock — для разработки, не для production).
 *
 * Город — обязательный параметр всех событий аналитики (см. CLAUDE.md).
 * Когда city=null, useAnalytics подставляет 'unknown' (защита от падения).
 *
 * Архитектурно разделено на:
 *  - resolveCity() — pure-функция, тестируется напрямую.
 *  - getInitialCity() — side-effecting wrapper, читает env + bridge.
 */

import { DEFAULT_CITY, isCity, type City } from '@/types/city';
import { getMaxBridge } from './max-bridge';

export type MaxMode = 'real' | 'mock';

export interface ResolveCityInput {
  /** start_param из MAX Bridge — для real-режима. */
  startParam?: string | undefined;
  /** Значение VITE_MOCK_CITY — для mock-режима. */
  mockCity?: string | undefined;
  /** В каком режиме определяем город. */
  mode: MaxMode;
}

/**
 * Pure-функция для определения города. Не имеет I/O — легко тестируется.
 *
 * Логика:
 *  - real-режим: validate(startParam) → null (no fallback). Frontend AppShell
 *    редиректит на /invalid-link при null. Лид не создаётся.
 *  - mock-режим: validate(mockCity) → DEFAULT_CITY (dev-only fallback).
 */
export function resolveCity({
  startParam,
  mockCity,
  mode,
}: ResolveCityInput): City | null {
  if (mode === 'real') {
    if (isCity(startParam)) return startParam;
    if (startParam !== undefined && startParam !== '') {
      // Невалидный — логируем для отладки. Не fallback'имся.
      console.warn(
        `[City] Invalid start_param "${startParam}" — city resolves to null, ` +
          `redirecting to /invalid-link`,
      );
    }
    return null;
  }

  // mock-режим — fallback на DEFAULT_CITY разрешён только здесь.
  if (isCity(mockCity)) return mockCity;
  return DEFAULT_CITY;
}

/**
 * Side-effecting обёртка: читает import.meta.env и bridge, возвращает
 * текущий город. В real-mode может вернуть null (нет валидного start_param).
 * В mock-mode всегда возвращает City (DEFAULT_CITY как fallback).
 *
 * Вызывается один раз при инициализации SessionContext.
 */
export function getInitialCity(): City | null {
  const mode = (import.meta.env.VITE_MAX_MODE ?? 'mock') as MaxMode;

  let startParam: string | undefined;
  if (mode === 'real') {
    try {
      startParam = getMaxBridge().initDataUnsafe?.start_param;
    } catch {
      // Bridge мог не инициализироваться (SSR/тесты) — в real-mode это null
      // (handle через resolveCity); в mock-mode уйдёт в DEFAULT_CITY.
    }
  }

  return resolveCity({
    startParam,
    mockCity: import.meta.env.VITE_MOCK_CITY,
    mode,
  });
}

/**
 * URL для CTA «Связаться с менеджером» — открывает бот в MAX с подставленным городом.
 * Точное имя бота возьмётся из VITE_MAX_BOT_USERNAME.
 */
export function getManagerContactUrl(city: City): string {
  const botUsername = import.meta.env.VITE_MAX_BOT_USERNAME || 'AyTiProfileBot';
  return `https://max.ru/${botUsername}?startapp=${city}`;
}
